import { SERVICE_CATEGORY_IDS } from '@powerful/config/categories';
import { SEARCH_AVAILABILITY, SEARCH_ESTABLISHMENT_TYPES, SEARCH_SORTS, searchRequestSchema, type SearchRequestInput } from '@powerful/types';

export interface DiscoveryFilters {
  query?: string;
  cityId?: string;
  radiusKm?: number;
  categoryIds?: string[];
  minRating?: number;
  priceMinCents?: number;
  priceMaxCents?: number;
  availability?: (typeof SEARCH_AVAILABILITY)[number];
  languages?: string[];
  types?: (typeof SEARCH_ESTABLISHMENT_TYPES)[number][];
  sort?: (typeof SEARCH_SORTS)[number];
}

export const DISCOVERY_SESSION_KEY = 'powerful.discovery.filters.v1';
type FilterParamKey = 'q' | 'city' | 'radius' | 'category' | 'rating' | 'priceMin' | 'priceMax' | 'availability' | 'language' | 'type' | 'sort';
const categoryIds = new Set<string>(SERVICE_CATEGORY_IDS);
const validCityId = (value: string) => /^[a-z]+(?:-[a-z]+)*$/.test(value);
const validNumber = (raw: string | null, min: number, max: number, integer = false): number | undefined => {
  if (raw === null || !/^(?:0|[1-9]\d*)(?:\.\d+)?$/.test(raw)) return undefined;
  const n = Number(raw);
  return Number.isFinite(n) && n >= min && n <= max && (!integer || Number.isInteger(n)) ? n : undefined;
};
const uniqueSorted = <T extends string>(values: T[]): T[] => [...new Set(values)].sort();

/** A URL prevalece campo a campo. Um parâmetro presente porém inválido não herda a sessão. */
export function parseDiscoveryFilters(search: string, sessionSearch = ''): DiscoveryFilters {
  const url = new URLSearchParams(search);
  const saved = new URLSearchParams(sessionSearch);
  // Links emitidos pelo app são snapshots completos: ausência de chave significa filtro limpo.
  // Links externos sem marcador ainda podem completar campos ausentes com a sessão.
  const source = (key: FilterParamKey) => url.get('v') === '1' || url.has(key) ? url : saved;
  const first = (key: FilterParamKey) => source(key).get(key);
  const query = first('q')?.trim().replace(/\s+/g, ' ').slice(0, 60) || undefined;
  const city = first('city');
  const radiusKm = validNumber(first('radius'), 1, 100);
  const category = uniqueSorted(source('category').getAll('category').filter((id) => categoryIds.has(id))).slice(0, 13);
  const minRating = validNumber(first('rating'), 1, 5);
  const priceMinCents = validNumber(first('priceMin'), 0, Number.MAX_SAFE_INTEGER, true);
  const priceMaxCents = validNumber(first('priceMax'), 0, Number.MAX_SAFE_INTEGER, true);
  const availabilityRaw = first('availability');
  const sortRaw = first('sort');
  const languages = uniqueSorted(source('language').getAll('language').filter((code) => /^[a-z]{2}$/.test(code))).slice(0, 12);
  const types = uniqueSorted(source('type').getAll('type').filter((type): type is (typeof SEARCH_ESTABLISHMENT_TYPES)[number] => SEARCH_ESTABLISHMENT_TYPES.includes(type as (typeof SEARCH_ESTABLISHMENT_TYPES)[number])));

  return {
    ...(query ? { query } : {}),
    ...(city && validCityId(city) ? { cityId: city } : {}),
    ...(radiusKm !== undefined ? { radiusKm } : {}),
    ...(category.length ? { categoryIds: category } : {}),
    ...(minRating !== undefined ? { minRating } : {}),
    ...(priceMinCents !== undefined ? { priceMinCents } : {}),
    ...(priceMaxCents !== undefined && (priceMinCents === undefined || priceMinCents <= priceMaxCents) ? { priceMaxCents } : {}),
    ...(availabilityRaw && SEARCH_AVAILABILITY.includes(availabilityRaw as (typeof SEARCH_AVAILABILITY)[number]) ? { availability: availabilityRaw as (typeof SEARCH_AVAILABILITY)[number] } : {}),
    ...(languages.length ? { languages } : {}),
    ...(types.length ? { types } : {}),
    ...(sortRaw && SEARCH_SORTS.includes(sortRaw as (typeof SEARCH_SORTS)[number]) ? { sort: sortRaw as (typeof SEARCH_SORTS)[number] } : {}),
  };
}

/** Ordem canônica para links, histórico e chaves do React Query. */
export function serializeDiscoveryFilters(filters: DiscoveryFilters): string {
  const p = new URLSearchParams();
  p.set('v', '1');
  if (filters.query?.trim()) p.set('q', filters.query.trim().replace(/\s+/g, ' ').slice(0, 60));
  if (filters.cityId) p.set('city', filters.cityId);
  if (filters.radiusKm !== undefined) p.set('radius', String(filters.radiusKm));
  for (const id of uniqueSorted(filters.categoryIds ?? [])) p.append('category', id);
  if (filters.minRating !== undefined) p.set('rating', String(filters.minRating));
  if (filters.priceMinCents !== undefined) p.set('priceMin', String(filters.priceMinCents));
  if (filters.priceMaxCents !== undefined) p.set('priceMax', String(filters.priceMaxCents));
  if (filters.availability) p.set('availability', filters.availability);
  for (const language of uniqueSorted(filters.languages ?? [])) p.append('language', language);
  for (const type of uniqueSorted(filters.types ?? [])) p.append('type', type);
  if (filters.sort && filters.sort !== 'relevance') p.set('sort', filters.sort);
  return p.toString();
}

export function readSessionFilters(storage: Pick<Storage, 'getItem'> = sessionStorage): string {
  try { return storage.getItem(DISCOVERY_SESSION_KEY) ?? ''; } catch { return ''; }
}

export function writeSessionFilters(filters: DiscoveryFilters, storage: Pick<Storage, 'setItem'> = sessionStorage): void {
  try { storage.setItem(DISCOVERY_SESSION_KEY, serializeDiscoveryFilters(filters)); } catch { /* sessão indisponível */ }
}

export type SearchOrigin = { lat: number; lng: number };

/** Somente campos aceitos pela Edge; coordenada arredondada antes de cruzar a rede. */
export function toSearchRequest(filters: DiscoveryFilters, origin?: SearchOrigin): SearchRequestInput {
  if (filters.sort === 'distance' && !origin) throw new Error('origin_required');
  const rounded = origin ? { lat: Number(origin.lat.toFixed(3)), lng: Number(origin.lng.toFixed(3)) } : undefined;
  const request: SearchRequestInput = {
    ...(filters.query ? { query: filters.query } : {}),
    ...(rounded ? { origin: rounded, ...(filters.radiusKm ? { radiusKm: filters.radiusKm } : {}) } : {}),
    ...(filters.categoryIds?.length ? { categoryIds: uniqueSorted(filters.categoryIds) } : {}),
    ...(filters.minRating !== undefined ? { minRating: filters.minRating } : {}),
    ...(filters.priceMinCents !== undefined ? { priceMinCents: filters.priceMinCents } : {}),
    ...(filters.priceMaxCents !== undefined ? { priceMaxCents: filters.priceMaxCents } : {}),
    ...(filters.availability ? { availability: filters.availability } : {}),
    ...(filters.languages?.length ? { languages: uniqueSorted(filters.languages) } : {}),
    ...(filters.types?.length ? { types: uniqueSorted(filters.types) } : {}),
    sort: filters.sort ?? 'relevance',
    limit: 20,
  };
  return searchRequestSchema.parse(request);
}

/** Exclui consulta e ordenação do badge de filtros. */
export function countActiveFilters(filters: DiscoveryFilters): number {
  return [filters.categoryIds?.length, filters.radiusKm, filters.minRating, filters.priceMinCents, filters.priceMaxCents, filters.availability, filters.languages?.length, filters.types?.length].filter(Boolean).length;
}
